<script lang="ts">
	// Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { routeToDeviceSettings } from 'src/stores/ui/route-to-device-settings';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';

	// Configs
	import { devices_config } from 'src/configs/devices';

	// Helpers
	import { real_id } from 'src/helpers/interface_helpers';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { hasVirtualGroups, isPillSerialMode } from 'src/helpers/thepill/thepill_data_helpers';
	import {
		getCriticalNotifications,
		getNotificationsForDevice,
	} from 'src/helpers/notifications/device_notifications_helper';
	import { useNavigateToHistoryStatisticsTab } from 'src/components/Sidepanel/useNavigateToHistoryStatisticsTab';

	// Design System Components
	import { Label } from '@Shelly/shelly-design-system';

	// Components
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import NoPeripheralsGuide from 'src/components/ThePill/NoPeripheralsGuide.svelte';
	import PillDrawerControl from 'src/components/ThePill/PillDrawerControl.svelte';
	import PillDrawerMainStatusV2 from 'src/components/ThePill/PillDrawerMainStatusV2.svelte';

	// Types
	import type { Device } from 'src/types/Device';
	import PillDrawerPeripheralsV2 from './PillDrawerPeripheralsV2.svelte';
	import DeviceNotificationLabel from '../Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceLabelList from '../Sidepanel/DeviceViews/DeviceLabelList.svelte';
	import {
		getGen1DeviceStatuses,
		createGen1StatusLabels,
	} from '../Sidepanel/SidePanelLayoutProvider/SidePanelDeviceLayout/Devices/Shelly/Gen1/gen1-main-helpers';
	import VGroupComponentsV2 from '../DevicesSettings/VirtualComponents/ComponentsV2/VGroupComponentsV2.svelte';

	export let device: Device;

	const { navigateToHistoryStatisticsTab } = useNavigateToHistoryStatisticsTab();
	const handleOnClickThumbChart = () => navigateToHistoryStatisticsTab();

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: deviceConfig = devices_config?.[device?.type];

	$: _statusesRaw =
		status?.online && deviceConfig ? status_configurator(status, device, settings) : [];
	$: statuses = Array.isArray(_statusesRaw) ? _statusesRaw : [];

	$: _statuses = getGen1DeviceStatuses(status, device, settings, deviceConfig);
	$: labelList = createGen1StatusLabels(_statuses);

	$: pillMode = settings?.pill?.mode || null;
	$: outputs = statuses.filter((i) => i.peripheralType === 'switch') || [];
	$: mainLabel = statuses.find((i) => i?.drawer_main);
	$: sensors = statuses.filter(
		(i) => i.peripheralType !== 'switch' && i.peripheralType !== 'humidity',
	);

	$: showVirtualGroups = isPillSerialMode(pillMode) && hasVirtualGroups(settings);
	$: showGuide =
		(!statuses?.length && !showVirtualGroups) ||
		(mainLabel?.value && mainLabel?.value.includes('N/A'));

	$: criticalNotifications = getCriticalNotifications(getNotificationsForDevice(device?.id));

	const handleOpenPeripheralsSettings = () => {
		routeToDeviceSettings.setTarget(
			device.id,
			'pill_peripheral_settings',
			'pill_peripheral_settings',
		);
		setSidePanelState({ showSettingsModal: true });
	};
</script>

<DeviceViewStateWrapper {device}>
	<div class="label-list-wrapper mb-s" slot="staticTop">
		{#if criticalNotifications?.length}
			<div class="d-flex justify-content-center mb-s max-width-full">
				<Label
					class="max-width-full"
					size="m"
					value={criticalNotifications[0].name}
					type={criticalNotifications[0].type === 'danger' ? 'danger' : 'warning'}
					icon="fa-solid fa-shield-exclamation"
				/>
			</div>
		{/if}
		<DeviceLabelList {labelList} />
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>
	<div slot="content">
		{#if showGuide}
			<NoPeripheralsGuide
				mode={pillMode}
				{device}
				{status}
				{settings}
				onPeripheralsSettingsClick={handleOpenPeripheralsSettings}
			/>
		{/if}
		{#if showVirtualGroups}
			<VGroupComponentsV2 {device} />
		{/if}
	</div>
	<div class="flex-full-height justify-content-end" slot="staticBottom">
		<PillDrawerPeripheralsV2 {sensors} on:click={handleOnClickThumbChart} />
		<PillDrawerMainStatusV2 {pillMode} {statuses} {mainLabel} />
		{#if outputs.length}
			<PillDrawerControl {device} {status} {outputs} useV2Components={true} />
		{/if}
	</div>
</DeviceViewStateWrapper>
